/**
 * Condition group helper — UI 측 복사본.
 *
 * 출처:
 *   hcltech-parts/packages/shared/src/condition/condition-group.ts
 *
 * UI 는 hcltech-parts 의 monorepo workspace 가 아니라 별도 repo 라 직접 import 불가.
 * 복사 패턴 — 동기화 부담은 있으나 외부 의존성 X.
 *
 * 미래에 mapping 룰 바뀌면 양쪽 같이 갱신 필요:
 *   ① hcltech-parts/packages/shared/src/condition/condition-group.ts
 *   ② hcltech-ui/src/lib/condition.ts                              (이 파일)
 *
 * 변경 시 주석에 timestamp 남겨두기.
 *
 * Last sync: 2026-06-22 (Sprint 1 — 멀티값 + F/S SPARE + 미등록 derived)
 */

export type ConditionGroup = '신품' | '벌크' | '중고' | '미확인';

// 고객 표기 기준 (2026-07 확정 — 3그룹). shared/condition-group.ts 와 동기화.
//   신품: F/S, F/S Spare, NOB / 벌크: Newpull, Newbulk, Bulk / 중고: REF
export const GROUP_MEMBERS: Record<ConditionGroup, string[]> = {
  '신품': [
    'F/S', 'F/S Spare', 'F/S SPARE', 'FS',
    'NOB', 'ENOB',
    'New', '신품',
  ],
  '벌크': [
    'Newpull', 'Newbulk', 'Bulk',
    'New Pull', 'New Bulk',
    '벌크',
  ],
  '중고': [
    'REF',
    'Used', 'Used/Bulk',
    '중고',
  ],
  '미확인': [],
};

const NORMALIZED_TO_GROUP: Map<string, ConditionGroup> = (() => {
  const map = new Map<string, ConditionGroup>();
  for (const [group, members] of Object.entries(GROUP_MEMBERS) as [ConditionGroup, string[]][]) {
    for (const m of members) {
      map.set(m.toLowerCase().trim(), group);
    }
  }
  map.set('미정', '미확인');
  // '미등록' 은 condition 이 아니라 품목 등록 상태 — isPartUnregistered 별도.
  return map;
})();

export function conditionGroup(raw: string | null | undefined): ConditionGroup {
  if (!raw) return '미확인';
  const key = raw.toLowerCase().trim();
  if (!key) return '미확인';
  return NORMALIZED_TO_GROUP.get(key) ?? '미확인';
}

export function conditionDisplay(raw: string | null | undefined): {
  group:  ConditionGroup;
  detail: string | null;
} {
  if (!raw || !raw.trim()) return { group: '미확인', detail: null };
  const trimmed = raw.trim();
  const group   = conditionGroup(trimmed);
  const isGroupName = trimmed === '신품' || trimmed === '벌크' || trimmed === '중고' || trimmed === '미확인' || trimmed === '미등록' || trimmed === '미정';
  return {
    group,
    detail: isGroupName ? null : trimmed,
  };
}

export function splitConditions(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}

export function conditionMembersForMatching(raw: string | null | undefined): string[] {
  const parts = splitConditions(raw);
  if (parts.length === 0) return [];
  const allMembers = new Set<string>();
  let anyRecognized = false;
  for (const part of parts) {
    const group = conditionGroup(part);
    if (group !== '미확인') {
      anyRecognized = true;
      for (const m of GROUP_MEMBERS[group]) allMembers.add(m);
    }
  }
  return anyRecognized ? Array.from(allMembers) : [];
}

export function conditionDisplayList(
  raw: string | null | undefined,
): Array<{ group: ConditionGroup; detail: string | null }> {
  const parts = splitConditions(raw);
  if (parts.length === 0) return [{ group: '미확인', detail: null }];
  return parts.map((p) => conditionDisplay(p));
}

/**
 * 품목 등록 상태 — option_part 가 NULL/빈 이면 미등록.
 * '미등록' 은 condition (제품 상태) 가 아닌 품목 마스터 등록 상태.
 */
export function isPartUnregistered(optionPart: string | null | undefined): boolean {
  return !optionPart || optionPart.trim() === '';
}
